<script setup>
import { computed, ref, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import {
  formatBytes,
  resolveMaximumFileUploadSize,
} from 'shared/helpers/FileHelper';
import {
  getAccountChannels,
  getAttachmentCompatibility,
  UNSUPPORTED_REASONS,
} from 'dashboard/helper/cannedResponseAttachments';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AttachmentThumb from './AttachmentThumb.vue';
import CannedAttachmentLibrary from './CannedAttachmentLibrary.vue';

const props = defineProps({
  // Entries are either a browser File (freshly picked from disk) or a library entry
  // carrying a signed_id, which points at a blob already in storage.
  newFiles: { type: Array, default: () => [] },
  savedAttachments: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:newFiles', 'removeSaved']);

// Kept in step with CannedResponse::MAX_ATTACHMENTS and ACCEPTED_CONTENT_TYPES.
const MAX_ATTACHMENTS = 5;
const ACCEPTED_EXTENSIONS = '.pdf,.jpg,.jpeg,.png,.webp,.mp4,.mov,.webm';
const ACCEPTED_CONTENT_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'video/mp4',
  'video/quicktime',
  'video/webm',
];

const { t } = useI18n();
const fileInputRef = ref(null);
const isLibraryOpen = ref(false);

const inboxes = useMapGetter('inboxes/getInboxes');
const globalConfig = useMapGetter('globalConfig/get');

const installationLimitMB = computed(() =>
  resolveMaximumFileUploadSize(globalConfig.value?.maximumFileUploadSize)
);

const channels = computed(() => getAccountChannels(inboxes.value));

// Object URLs for files not uploaded yet. Cached so a re-render reuses the same URL
// instead of leaking a new one, and released when the form goes away.
const objectUrls = new Map();
const previewUrlFor = file => {
  if (!objectUrls.has(file)) objectUrls.set(file, URL.createObjectURL(file));
  return objectUrls.get(file);
};
onBeforeUnmount(() => {
  objectUrls.forEach(url => URL.revokeObjectURL(url));
  objectUrls.clear();
});

const describeWarning = result => {
  if (result.reason === UNSUPPORTED_REASONS.TYPE) {
    return t('CANNED_MGMT.ATTACHMENTS.COMPATIBILITY.UNSUPPORTED_TYPE', {
      channel: result.label,
    });
  }
  return t('CANNED_MGMT.ATTACHMENTS.COMPATIBILITY.TOO_BIG_FOR_CHANNEL', {
    channel: result.label,
    size: result.maxSizeMB,
  });
};

const decorate = item => ({
  ...item,
  warnings: getAttachmentCompatibility(
    item,
    channels.value,
    installationLimitMB.value
  )
    .filter(result => !result.supported)
    .map(describeWarning),
});

const describeEntry = (entry, index) => {
  if (entry instanceof File) {
    return {
      key: `new-${index}-${entry.name}`,
      index,
      filename: entry.name,
      contentType: entry.type,
      byteSize: entry.size,
      previewUrl: previewUrlFor(entry),
    };
  }
  return {
    key: `lib-${index}-${entry.signed_id}`,
    index,
    signedId: entry.signed_id,
    filename: entry.filename,
    contentType: entry.content_type,
    byteSize: entry.byte_size,
    previewUrl: entry.data_url,
  };
};

const items = computed(() => [
  ...props.savedAttachments.map(attachment =>
    decorate({
      key: `saved-${attachment.id}`,
      id: attachment.id,
      saved: true,
      signedId: attachment.signed_id,
      filename: attachment.filename,
      contentType: attachment.content_type,
      byteSize: attachment.byte_size,
      previewUrl: attachment.data_url,
    })
  ),
  ...props.newFiles.map((entry, index) =>
    decorate({ ...describeEntry(entry, index), saved: false })
  ),
]);

const isFull = computed(() => items.value.length >= MAX_ATTACHMENTS);

const allSupported = computed(
  () =>
    items.value.length > 0 && items.value.every(item => !item.warnings.length)
);

// Blobs already on this response; ActiveStorage rejects attaching the same one twice.
const attachedSignedIds = computed(() =>
  items.value.map(item => item.signedId).filter(Boolean)
);

const openFileDialog = () => fileInputRef.value?.click();

const handleFileChange = event => {
  const selected = [...event.target.files];
  // Cleared so picking the same file again still fires a change event.
  event.target.value = '';

  const room = MAX_ATTACHMENTS - items.value.length;
  if (room <= 0) {
    useAlert(
      t('CANNED_MGMT.ATTACHMENTS.MAX_FILES', { count: MAX_ATTACHMENTS })
    );
    return;
  }

  const errors = [];
  const accepted = [];

  selected.slice(0, room).forEach(file => {
    if (!ACCEPTED_CONTENT_TYPES.includes(file.type)) {
      errors.push(
        t('CANNED_MGMT.ATTACHMENTS.INVALID_TYPE', { filename: file.name })
      );
      return;
    }
    if (file.size > installationLimitMB.value * 1024 * 1024) {
      errors.push(
        t('CANNED_MGMT.ATTACHMENTS.TOO_BIG', {
          filename: file.name,
          size: installationLimitMB.value,
        })
      );
      return;
    }
    accepted.push(file);
  });

  if (selected.length > room) {
    errors.push(
      t('CANNED_MGMT.ATTACHMENTS.MAX_FILES', { count: MAX_ATTACHMENTS })
    );
  }

  if (accepted.length) emit('update:newFiles', [...props.newFiles, ...accepted]);
  if (errors.length) useAlert(errors.join(' '));
};

const addFromLibrary = file => {
  if (isFull.value) {
    useAlert(
      t('CANNED_MGMT.ATTACHMENTS.MAX_FILES', { count: MAX_ATTACHMENTS })
    );
    return;
  }
  emit('update:newFiles', [...props.newFiles, file]);
};

const removeItem = item => {
  if (item.saved) {
    emit('removeSaved', item.id);
    return;
  }
  const entry = props.newFiles[item.index];
  if (entry instanceof File && objectUrls.has(entry)) {
    URL.revokeObjectURL(objectUrls.get(entry));
    objectUrls.delete(entry);
  }
  emit(
    'update:newFiles',
    props.newFiles.filter((_, index) => index !== item.index)
  );
};
</script>

<template>
  <div class="w-full">
    <div class="flex items-center justify-between gap-2">
      <label class="mb-0">{{ $t('CANNED_MGMT.ATTACHMENTS.LABEL') }}</label>
      <div class="flex items-center gap-1">
        <NextButton
          faded
          slate
          xs
          type="button"
          icon="i-ri-folder-open-line"
          :label="$t('CANNED_MGMT.ATTACHMENTS.LIBRARY.BUTTON')"
          @click.prevent="isLibraryOpen = !isLibraryOpen"
        />
        <NextButton
          faded
          slate
          xs
          type="button"
          icon="i-ri-attachment-2"
          :label="$t('CANNED_MGMT.ATTACHMENTS.ADD_BUTTON')"
          :disabled="isFull"
          @click.prevent="openFileDialog"
        />
      </div>
    </div>
    <p class="mt-1 mb-2 text-xs text-n-slate-11">
      {{ $t('CANNED_MGMT.ATTACHMENTS.HELP', { count: MAX_ATTACHMENTS }) }}
    </p>

    <CannedAttachmentLibrary
      v-if="isLibraryOpen"
      :attached-signed-ids="attachedSignedIds"
      :full="isFull"
      @select="addFromLibrary"
    />

    <ul v-if="items.length" class="flex flex-col gap-1.5 p-0 mb-1 list-none">
      <li
        v-for="item in items"
        :key="item.key"
        class="flex flex-col gap-1 p-1.5 border rounded-lg border-n-strong"
      >
        <div class="flex items-center gap-2">
          <AttachmentThumb
            :filename="item.filename"
            :content-type="item.contentType"
            :preview-url="item.previewUrl"
          />

          <div class="flex-1 min-w-0">
            <p class="mb-0 text-sm truncate text-n-slate-12">
              {{ item.filename }}
            </p>
            <p class="mb-0 text-xs text-n-slate-11">
              {{ formatBytes(item.byteSize, 1) }}
            </p>
          </div>

          <NextButton
            ghost
            ruby
            xs
            icon="i-ri-delete-bin-line"
            :title="$t('CANNED_MGMT.ATTACHMENTS.REMOVE')"
            @click.prevent="removeItem(item)"
          />
        </div>

        <p
          v-for="warning in item.warnings"
          :key="warning"
          class="flex items-center gap-1 m-0 text-xs text-n-amber-11"
        >
          <span class="i-ri-error-warning-line shrink-0" />
          {{ warning }}
        </p>
      </li>
    </ul>

    <p v-if="allSupported" class="mb-0 text-xs text-n-teal-11">
      {{ $t('CANNED_MGMT.ATTACHMENTS.COMPATIBILITY.ALL_SUPPORTED') }}
    </p>

    <input
      ref="fileInputRef"
      type="file"
      multiple
      :accept="ACCEPTED_EXTENSIONS"
      class="hidden"
      @change="handleFileChange"
    />
  </div>
</template>
